import { Head, Link, router } from '@inertiajs/react'
import { ChangeEvent, DragEvent, useRef, useState } from 'react'
import Layout from '../../../components/Layout'
import type { Batch, OrganizationRef } from './types'

interface Props {
  organization: OrganizationRef
  batches: Batch[]
  errors?: Record<string, string | string[]>
}

const supported = ['Scorecards (.csv)', 'Rocks & milestones (.xlsx)', 'To-dos (.xlsx)', 'Issues (.xlsx)', 'Headlines (.xlsx)']

function dateLabel(value?: string) {
  return value ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value)) : '—'
}

function statusStyle(status: string) {
  if (status === 'committed') return 'bg-[#E8F3EB] text-[#15532E]'
  if (status === 'failed') return 'bg-[#FBEAEA] text-pinnacle-red'
  return 'bg-[#F6F0E7] text-[#6D552C]'
}

export default function NinetyImportsIndex({ organization, batches, errors }: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [files, setFiles] = useState<File[]>([])
  const [dragging, setDragging] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const addFiles = (incoming: FileList | null) => {
    if (!incoming) return
    setFiles(current => [...current, ...Array.from(incoming)].filter((file, index, list) => list.findIndex(item => item.name === file.name && item.size === file.size) === index))
  }

  const upload = () => {
    if (!files.length) return
    setSubmitting(true)
    const data = new FormData()
    files.forEach(file => data.append('files[]', file))
    router.post('/admin/ninety_imports', data, {
      onFinish: () => setSubmitting(false)
    })
  }

  const drop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setDragging(false)
    addFiles(event.dataTransfer.files)
  }

  return (
    <Layout>
      <Head title={`Import Ninety exports · ${organization.name}`} />
      <div className="max-w-5xl mx-auto pb-12">
        <nav className="mb-6 text-sm text-text-secondary" aria-label="Breadcrumb">
          <Link href="/admin/settings" className="hover:text-text-primary">Company administration</Link>
          <span className="mx-2 text-stone/50">/</span>
          <span className="text-text-primary">Import from Ninety</span>
        </nav>
        <header className="border-l-4 border-pinnacle-red pl-5 mb-9">
          <p className="text-sm font-semibold uppercase tracking-[0.16em] text-pinnacle-red">Migration workspace</p>
          <h1 className="font-heading text-3xl font-bold text-text-primary mt-1">Bring your Ninety history into {organization.name}</h1>
          <p className="text-base text-text-secondary mt-2 max-w-3xl">Stage the original exports together, review every proposed record, then make one validated import. Nothing changes in your company until you confirm.</p>
        </header>

        <section className="bg-white rounded-xl shadow-card overflow-hidden mb-8" aria-labelledby="upload-title">
          <div className="p-6 border-b border-stone/10">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 shrink-0 rounded-lg bg-pinnacle-red/10 text-pinnacle-red flex items-center justify-center font-heading font-bold">01</div>
              <div>
                <h2 id="upload-title" className="font-heading text-xl font-semibold text-text-primary">Stage original exports</h2>
                <p className="text-text-secondary mt-1">Upload CSV scorecards and the Ninety workbooks. We detect exports by their structure, not their filename.</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            {errors && Object.keys(errors).length > 0 && (
              <div role="alert" className="mb-5 rounded-lg border border-pinnacle-red/20 bg-[#FBEAEA] px-4 py-3 text-sm text-[#8D2020]">
                {Object.entries(errors).map(([field, messages]) => <p key={field}>{Array.isArray(messages) ? messages.join(', ') : messages}</p>)}
              </div>
            )}
            <div
              onDragOver={(event) => { event.preventDefault(); setDragging(true) }}
              onDragLeave={() => setDragging(false)}
              onDrop={drop}
              className={`rounded-xl border-2 border-dashed p-9 text-center transition-colors ${dragging ? 'border-pinnacle-red bg-pinnacle-red/5' : 'border-stone/25 bg-[#FCFAF7]'}`}
            >
              <input ref={inputRef} onClick={(event) => { event.currentTarget.value = '' }} onChange={(event: ChangeEvent<HTMLInputElement>) => addFiles(event.target.files)} type="file" accept=".csv,.xlsx" multiple className="sr-only" />
              <div className="mx-auto mb-3 w-11 h-11 rounded-full bg-charcoal/5 flex items-center justify-center text-text-primary">
                <svg aria-hidden="true" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M7 16a4 4 0 01-.88-7.903A5.5 5.5 0 0116.5 7a4.5 4.5 0 011.5 8.742M12 12v9m0-9l-3 3m3-3l3 3" /></svg>
              </div>
              <p className="font-medium text-text-primary">Drop export files here, or <button type="button" onClick={() => inputRef.current?.click()} className="text-pinnacle-red underline underline-offset-2">browse from your computer</button></p>
              <p className="text-sm text-text-secondary mt-1">CSV and XLSX only. Files are kept only for this import review.</p>
            </div>
            {files.length > 0 && <div className="mt-4 space-y-2" aria-live="polite">{files.map(file => <div key={`${file.name}-${file.size}`} className="flex items-center justify-between rounded-lg bg-[#F7F4EF] px-4 py-2.5 text-sm"><span className="truncate text-text-primary">{file.name} <span className="text-text-secondary">({Math.ceil(file.size / 1024)} KB)</span></span><button type="button" onClick={() => setFiles(current => current.filter(item => item !== file))} className="ml-4 text-text-secondary hover:text-pinnacle-red">Remove</button></div>)}</div>}
            <div className="mt-5 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4">
              <p className="text-xs text-text-secondary">Supported: {supported.join(' · ')}</p>
              <button type="button" onClick={upload} disabled={!files.length || submitting} className="bg-pinnacle-red hover:bg-pinnacle-red/90 disabled:opacity-50 text-white font-semibold px-5 py-2.5 rounded-lg transition-colors">{submitting ? 'Staging files…' : 'Stage and review files'}</button>
            </div>
          </div>
        </section>

        <section aria-labelledby="history-title">
          <div className="flex items-baseline justify-between mb-4"><h2 id="history-title" className="font-heading text-xl font-semibold text-text-primary">Import history</h2><span className="text-sm text-text-secondary">{batches.length} {batches.length === 1 ? 'batch' : 'batches'}</span></div>
          {batches.length === 0 ? <div className="rounded-xl border border-dashed border-stone/25 bg-white/60 px-6 py-10 text-center text-text-secondary">No Ninety exports have been staged for this company yet.</div> : (
            <div className="bg-white rounded-xl shadow-card overflow-hidden divide-y divide-stone/10">
              {batches.map(batch => <Link key={batch.id} href={`/admin/ninety_imports/${batch.id}`} className="block p-5 hover:bg-[#FCFAF7] transition-colors">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3"><div><div className="flex gap-2 items-center"><span className={`text-xs font-semibold uppercase tracking-wide rounded-full px-2.5 py-1 ${statusStyle(batch.status)}`}>{batch.status}</span><span className="text-sm text-text-secondary">Batch #{batch.id}</span></div><p className="text-sm text-text-secondary mt-2">Staged {dateLabel(batch.created_at)}{batch.committed_at ? ` · imported ${dateLabel(batch.committed_at)}` : ''}</p></div><span className="text-sm font-medium text-pinnacle-red">Open review <span aria-hidden="true">→</span></span></div>
              </Link>)}
            </div>
          )}
        </section>
      </div>
    </Layout>
  )
}